<template>
  <div id="app">
    <template v-if="isLoading">
      <!-- Show loading screen while Auth0 is initializing -->
      <div class="app-loading">
        <div class="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    </template>
    <template v-else-if="isAuthenticated || $route.meta.allowAnonymous">
      <TopNavigation v-if="isAuthenticated && !$route.meta.hideNavigation" />
      <router-view></router-view>
      <BottomNavigation v-if="isAuthenticated && !$route.meta.hideNavigation" />
    </template>
    <template v-else>
      <LoginPage />
    </template>
  </div>
</template>

<script>
// Import components
import TopNavigation from '@/components/TopNavigation.vue'
import BottomNavigation from '@/components/BottomNavigation.vue'
import LoginPage from '@/views/LoginPage.vue'
import { useAuth0 } from '@auth0/auth0-vue'

export default {
  name: 'App',
  components: {
    TopNavigation,
    BottomNavigation,
    LoginPage
  },
  setup() {
    const { isAuthenticated, isLoading } = useAuth0()
    return { isAuthenticated, isLoading }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --primary: #10b981;
  --primary-dark: #059669;
  --primary-light: #d1fae5;
  --secondary: #6366F1;
  --success: #22C55E;
  --warning: #F59E0B;
  --error: #EF4444;
  --background: #FAFAFA;
  --surface: #FFFFFF;
  --text-primary: #111827;
  --text-secondary: #6B7280;
  --border: #E5E7EB;
  --navbar-height: 60px;
}

html, body {
  height: 100%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  line-height: 1.6;
  color: var(--text-primary);
  background-color: var(--background);
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.container {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  border: none;
  gap: 0.5rem;
}

.btn-primary {
  background-color: var(--primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
}

.btn-secondary {
  background-color: #f3f4f6;
  color: var(--text-primary);
}

.btn-secondary:hover {
  background-color: #e5e7eb;
}

.btn-large {
  padding: 1rem 2rem;
  font-size: 1.125rem;
}

.card {
  background-color: var(--surface);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  margin-bottom: 1rem;
}

.section-header {
  margin-bottom: 1.5rem;
  padding: 0 1rem;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 2rem;
  font-weight: 600;
  font-size: 0.75rem;
}

.badge-success {
  background-color: rgba(34, 197, 94, 0.1);
  color: var(--success);
}

.badge-warning {
  background-color: rgba(245, 158, 11, 0.1);
  color: var(--warning);
}

.badge-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: var(--error);
}

.badge-info {
  background-color: rgba(99, 102, 241, 0.1);
  color: var(--secondary);
}

h1, h2, h3, h4, h5, h6 {
  line-height: 1.3;
  margin-bottom: 1rem;
  color: var(--text-primary);
}

h1 {
  font-size: 1.75rem;
}

h2 {
  font-size: 1.5rem;
}

h3 {
  font-size: 1.25rem;
}

p {
  margin-bottom: 1rem;
}

/* Page transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Adjust for top and bottom navigation */
.page-content {
  padding-bottom: calc(var(--navbar-height) + 1rem);
}

/* App loading screen */
.app-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: var(--background);
  color: var(--text-secondary);
}

.app-loading .loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(16, 185, 129, 0.2);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s infinite linear;
  margin-bottom: 1rem;
}

.app-loading p {
  font-size: 0.875rem;
  margin: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>